import { DOCUMENT_KIND, DOCUMENT_STATE, isReceived } from '../documents';
import { asDate } from '../utils';
import Icon from './Icon';
import type { Doc } from '../types';

/**
 * THE FACE OF A DOCUMENT ROW — one source of truth for how a document reads in a
 * list, so the Documents screen and the project's Plan can never drift apart
 * again (they had grown two densities and two treatments of the same row).
 *
 * The row reads as ALIGNED COLUMNS so the eye runs straight lines down a long
 * list: a quiet kind label, the title, what it covers, the date it was made, and
 * one status chip. The kind and covers are plain labels — the status is the only
 * chip, so it is the only thing that shouts. The status sits in a fixed-width
 * right cell so the column edge stays straight while the chip itself hugs its
 * text (the app's standard rounded-square chip, no dead space).
 *
 * The caller wraps this in the control that does the opening: the Documents
 * screen expands the row in place, the Plan drills to the document. Both wrap it
 * in `.doc-line` inside a `.doc-row`, so the look is one thing defined once.
 *
 * A received/reference document is kept and read, never signed here (0111), so it
 * wears a "Received" tag in place of a sign-off state — but only once a file has
 * actually arrived. "Received" is a fact about a document that was handed to you;
 * an empty record nobody has uploaded to yet is "Awaiting file", not received.
 * Vinnie, 16 September 2026: it must not say received just because it was created.
 */
/**
 * THE STATUS CHIP — the one thing on a document that shouts, defined once so the
 * list row and the opened-document header read identically. A received/reference
 * document wears a "Received"/"Awaiting file" tag in place of a sign-off state; a
 * rejected sheet leaves doc.state at 'awaiting' (rejection lives on the sign-off
 * rows) so the caller passes it in and the chip says "Not approved"; a signed-off
 * document carries the finished tick.
 */
export function DocStatus({ doc, rejected }: { doc: Doc; rejected?: boolean }) {
  if (isReceived(doc.kind)) {
    return doc.filePath ? (
      <span className="doc-received">Received</span>
    ) : (
      <span className="doc-received is-awaiting">Awaiting file</span>
    );
  }
  if (rejected && doc.state === 'awaiting') {
    return <span className="doc-state is-rejected">Not approved</span>;
  }
  return (
    <span className={`doc-state is-${doc.state}`}>
      {doc.state === 'signed' && <Icon name="check" size={11} className="doc-state-tick" />}
      {DOCUMENT_STATE[doc.state]}
    </span>
  );
}

export function DocFace({ doc, covers, rejected }: { doc: Doc; covers?: string; rejected?: boolean }) {
  return (
    <>
      <span className="doc-kind">{DOCUMENT_KIND[doc.kind]}</span>
      <span className="doc-title">{doc.title}</span>
      {covers && <span className="doc-covers">{covers}</span>}
      {/* SHOWN ONLY ONCE THERE IS MORE THAN ONE VERSION — a "v1" on every row is a
          number that never changes and reads as clutter; it returns when a real
          chain exists. */}
      {doc.version > 1 && <span className="doc-version">v{doc.version}</span>}
      {doc.createdAt ? <span className="doc-date">{asDate(doc.createdAt)}</span> : <span className="doc-date" />}
      <span className="doc-status">
        <DocStatus doc={doc} rejected={rejected} />
      </span>
    </>
  );
}

/**
 * THE OPENED-DOCUMENT HEADER — the titled header a document wears when it is open
 * (the form-pattern `fd-head`, Vinnie approved 3 Oct 2026). In the list a document
 * reads as an aligned column row (DocFace); once opened it is a titled document,
 * not another grid row — a real title, a quiet meta line (project · what it covers
 * · when it was made) and its one status chip. The duplicated kind+title the old
 * header showed is gone: the title says what it is.
 */
export function DocFormHeader({
  doc,
  covers,
  rejected,
  project,
}: {
  doc: Doc;
  covers?: string;
  rejected?: boolean;
  project?: string;
}) {
  const sub = [project, covers, doc.createdAt ? `Created ${asDate(doc.createdAt)}` : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <header className="fd-head">
      <div className="fd-head-main">
        <h2 className="fd-title">{doc.title}</h2>
        {sub && <p className="fd-sub">{sub}</p>}
      </div>
      <div className="fd-head-side">
        <DocStatus doc={doc} rejected={rejected} />
      </div>
    </header>
  );
}
